import { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Switch } from "@akuna/ui";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { TemplatePreview } from "@/components/whatsapp/TemplatePreview";
import { useWhatsAppMessageTemplates } from "@/hooks/useWhatsAppMessageTemplates";
import { useAudiencePreview } from "@/hooks/useAudiencePreview";
import { useCreateCampaign } from "@/hooks/useCampaigns";
import { useBreeds, useCoatTypes, useSpecies } from "@/hooks/useLookups";
import { usePetSizeCategories } from "@/hooks/usePetGroomingProfile";
import type { AudienceFilter } from "@/lib/campaigns/audience-query";
import type { ExplorerAbcCurve, AgeGroup } from "@/hooks/useBaseExplorer";
import { estimateCampaignCost } from "@/lib/meta/pricing";
import type { MetaTemplateCategory } from "@/lib/meta/pricing";
import { cn } from "@/lib/utils";

const STEPS = ["stepAudience", "stepTemplate", "stepSchedule", "stepReview"] as const;

// --- Audience Step ---
function AudienceStep({
  filter,
  onChange,
}: {
  filter: AudienceFilter;
  onChange: (f: AudienceFilter) => void;
}) {
  const { t } = useTranslation("campaigns");
  const { data: species = [] } = useSpecies();
  const { data: breeds = [] } = useBreeds();
  const { data: coatTypes = [] } = useCoatTypes();
  const { data: sizes = [] } = usePetSizeCategories();
  const { count, insights, isLoading } = useAudiencePreview(filter);

  const abcOptions: ExplorerAbcCurve[] = ["A", "B", "C"];
  const ageOptions: AgeGroup[] = ["under1", "1to7", "7plus", "unknown"];

  function toggleArr<T>(arr: T[] | undefined, val: T): T[] {
    const current = arr ?? [];
    return current.includes(val) ? current.filter((v) => v !== val) : [...current, val];
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold">{t("audienceTitle")}</h3>
        <p className="text-sm text-muted-foreground">{t("audienceDesc")}</p>
      </div>

      {/* WhatsApp only */}
      <div className="flex items-center gap-3">
        <Switch
          checked={filter.hasWhatsApp ?? false}
          onCheckedChange={(v) => onChange({ ...filter, hasWhatsApp: v || undefined })}
        />
        <Label>{t("filterHasWhatsApp")}</Label>
      </div>

      {/* ABC curve */}
      <div className="space-y-2">
        <Label>{t("filterAbc")}</Label>
        <div className="flex flex-wrap gap-2">
          {abcOptions.map((curve) => (
            <button
              key={curve}
              type="button"
              onClick={() => onChange({ ...filter, abcCurves: toggleArr(filter.abcCurves, curve) })}
              className={cn(
                "rounded-full px-3 py-1 text-xs border transition-colors",
                filter.abcCurves?.includes(curve)
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border hover:bg-muted"
              )}
            >
              {curve}
            </button>
          ))}
        </div>
      </div>

      {/* Species */}
      {species.length > 0 && (
        <div className="space-y-2">
          <Label>{t("filterSpecies")}</Label>
          <div className="flex flex-wrap gap-2">
            {species.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => onChange({ ...filter, speciesIds: toggleArr(filter.speciesIds, s.id) })}
                className={cn(
                  "rounded-full px-3 py-1 text-xs border transition-colors",
                  filter.speciesIds?.includes(s.id)
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border hover:bg-muted"
                )}
              >
                {s.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Breeds */}
      {breeds.length > 0 && (
        <div className="space-y-2">
          <Label>{t("filterBreed")}</Label>
          <div className="flex flex-wrap gap-2">
            {breeds
              .filter((breed) => !filter.speciesIds?.length || filter.speciesIds.includes(breed.species_id))
              .map((breed) => (
                <button
                  key={breed.id}
                  type="button"
                  onClick={() => onChange({ ...filter, breedIds: toggleArr(filter.breedIds, breed.id) })}
                  className={cn(
                    "rounded-full px-3 py-1 text-xs border transition-colors",
                    filter.breedIds?.includes(breed.id)
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border hover:bg-muted"
                  )}
                >
                  {breed.name}
                </button>
              ))}
          </div>
        </div>
      )}

      {/* Size */}
      {sizes.length > 0 && (
        <div className="space-y-2">
          <Label>{t("filterSize")}</Label>
          <div className="flex flex-wrap gap-2">
            {sizes.map((size) => (
              <button
                key={size.id}
                type="button"
                onClick={() => onChange({ ...filter, sizeCategoryIds: toggleArr(filter.sizeCategoryIds, size.id) })}
                className={cn(
                  "rounded-full px-3 py-1 text-xs border transition-colors",
                  filter.sizeCategoryIds?.includes(size.id)
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border hover:bg-muted"
                )}
              >
                {size.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Coat type */}
      {coatTypes.length > 0 && (
        <div className="space-y-2">
          <Label>{t("filterCoatType")}</Label>
          <div className="flex flex-wrap gap-2">
            {coatTypes.map((coatType) => (
              <button
                key={coatType.id}
                type="button"
                onClick={() => onChange({ ...filter, coatTypeIds: toggleArr(filter.coatTypeIds, coatType.id) })}
                className={cn(
                  "rounded-full px-3 py-1 text-xs border transition-colors",
                  filter.coatTypeIds?.includes(coatType.id)
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border hover:bg-muted"
                )}
              >
                {coatType.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Age group */}
      <div className="space-y-2">
        <Label>{t("filterAgeGroup")}</Label>
        <div className="flex flex-wrap gap-2">
          {ageOptions.map((ag) => {
            const label = t(`ageGroup.${ag}`);
            return (
              <button
                key={ag}
                type="button"
                onClick={() => onChange({ ...filter, ageGroups: toggleArr(filter.ageGroups, ag) })}
                className={cn(
                  "rounded-full px-3 py-1 text-xs border transition-colors",
                  filter.ageGroups?.includes(ag)
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border hover:bg-muted"
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <Label>{t("filterAgeRange")}</Label>
        <div className="flex items-center gap-3">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">{t("filterAgeMin")}</p>
            <Input
              type="number"
              min={0}
              className="w-24 text-sm"
              inputSize="sm"
              value={filter.ageYearsMin ?? ""}
              onChange={(e) =>
                onChange({ ...filter, ageYearsMin: e.target.value ? Number(e.target.value) : undefined })
              }
            />
          </div>
          <span className="text-muted-foreground mt-5">—</span>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">{t("filterAgeMax")}</p>
            <Input
              type="number"
              min={0}
              className="w-24 text-sm"
              inputSize="sm"
              value={filter.ageYearsMax ?? ""}
              onChange={(e) =>
                onChange({ ...filter, ageYearsMax: e.target.value ? Number(e.target.value) : undefined })
              }
            />
          </div>
        </div>
      </div>

      {/* Days since visit */}
      <div className="space-y-2">
        <Label>{t("filterDaysSinceVisit")}</Label>
        <div className="flex items-center gap-3">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">{t("filterDaysSinceVisitMin")}</p>
            <Input
              type="number"
              min={0}
              className="w-24 text-sm"
              inputSize="sm"
              value={filter.daysSinceVisitMin ?? ""}
              onChange={(e) =>
                onChange({ ...filter, daysSinceVisitMin: e.target.value ? Number(e.target.value) : undefined })
              }
            />
          </div>
          <span className="text-muted-foreground mt-5">—</span>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">{t("filterDaysSinceVisitMax")}</p>
            <Input
              type="number"
              min={0}
              className="w-24 text-sm"
              inputSize="sm"
              value={filter.daysSinceVisitMax ?? ""}
              onChange={(e) =>
                onChange({ ...filter, daysSinceVisitMax: e.target.value ? Number(e.target.value) : undefined })
              }
            />
          </div>
        </div>
      </div>

      {/* Preview count */}
      <Card className="bg-muted/30">
        <CardContent className="py-4">
          <div className="mb-3 flex items-center gap-3">
            <span className="text-sm font-medium">{t("audiencePreview")}:</span>
            {isLoading ? (
              <span className="text-sm text-muted-foreground">{t("audiencePreviewLoading")}</span>
            ) : (
              <Badge variant="secondary" className="text-sm">
                {t("audiencePreviewCount", { count: count ?? 0, defaultValue_other: `${count ?? 0} tutores` })}
              </Badge>
            )}
          </div>
          {!isLoading && insights && (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <div className="rounded-md border bg-background/70 p-2">
                <p className="text-xs text-muted-foreground">{t("audiencePets")}</p>
                <p className="text-lg font-semibold tabular-nums">{insights.petCount}</p>
              </div>
              <div className="rounded-md border bg-background/70 p-2">
                <p className="text-xs text-muted-foreground">{t("audienceHistoricalSpent")}</p>
                <p className="text-lg font-semibold tabular-nums">
                  {new Intl.NumberFormat(undefined, { style: "currency", currency: "BRL" }).format(insights.totalHistoricalSpent)}
                </p>
              </div>
              <div className="rounded-md border bg-background/70 p-2">
                <p className="text-xs text-muted-foreground">{t("audienceWhatsappReady")}</p>
                <p className="text-lg font-semibold tabular-nums">{insights.whatsappCount}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// --- Template Step ---
function TemplateStep({
  selectedId,
  onSelect,
}: {
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation("campaigns");
  const { data: templates = [] } = useWhatsAppMessageTemplates();
  const approved = templates.filter((tpl) => tpl.metaStatus === "APPROVED");
  const selected = approved.find((tpl) => tpl.id === selectedId) ?? null;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold">{t("templateTitle")}</h3>
        <p className="text-sm text-muted-foreground">{t("templateDesc")}</p>
      </div>
      {approved.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("templateNoApproved")}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
            {approved.map((tpl) => (
              <button
                key={tpl.id}
                type="button"
                onClick={() => onSelect(tpl.id)}
                className={cn(
                  "w-full text-left rounded-lg border p-3 transition-colors",
                  tpl.id === selectedId
                    ? "border-primary bg-primary/5"
                    : "border-border hover:bg-muted/50"
                )}
              >
                <p className="font-mono text-sm font-medium">{tpl.name}</p>
                <p className="text-xs text-muted-foreground truncate mt-0.5">{tpl.bodyText}</p>
              </button>
            ))}
          </div>
          {selected && (
            <TemplatePreview template={selected} />
          )}
        </div>
      )}
    </div>
  );
}

// --- Schedule Step ---
function ScheduleStep({
  scheduledAt,
  onChange,
}: {
  scheduledAt: string | null;
  onChange: (v: string | null) => void;
}) {
  const { t } = useTranslation("campaigns");
  const [mode, setMode] = useState<"now" | "later">(scheduledAt ? "later" : "now");

  function handleModeChange(v: "now" | "later") {
    setMode(v);
    if (v === "now") onChange(null);
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold">{t("scheduleTitle")}</h3>
        <p className="text-sm text-muted-foreground">{t("scheduleDesc")}</p>
      </div>
      <RadioGroup value={mode} onValueChange={(v) => handleModeChange(v as "now" | "later")}>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="now" id="sched-now" />
          <Label htmlFor="sched-now">{t("scheduleNow")}</Label>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="later" id="sched-later" />
          <Label htmlFor="sched-later">{t("scheduleLater")}</Label>
        </div>
      </RadioGroup>
      {mode === "later" && (
        <div className="space-y-1">
          <Label>{t("scheduleDateLabel")}</Label>
          <Input
            type="datetime-local"
            className="w-64"
            value={scheduledAt ? scheduledAt.slice(0, 16) : ""}
            onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : null)}
          />
        </div>
      )}
    </div>
  );
}

// --- Review Step ---
function ReviewStep({
  name,
  onNameChange,
  audienceCount,
  templateName,
  scheduledAt,
  estimatedCost,
}: {
  name: string;
  onNameChange: (v: string) => void;
  audienceCount: number | null;
  templateName: string | null;
  scheduledAt: string | null;
  estimatedCost: number | null;
}) {
  const { t } = useTranslation("campaigns");
  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold">{t("reviewTitle")}</h3>
      </div>
      <div className="space-y-2">
        <Label>{t("tableNameCol")}</Label>
        <Input value={name} onChange={(e) => onNameChange(e.target.value)} className="max-w-sm" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t("reviewAudience")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold tabular-nums">{audienceCount?.toLocaleString() ?? "—"}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t("reviewTemplate")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-mono text-sm truncate">{templateName ?? "—"}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t("reviewSchedule")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">
              {scheduledAt
                ? new Date(scheduledAt).toLocaleString("pt-BR")
                : t("reviewScheduleNow")}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t("reviewEstimatedCost")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold tabular-nums">
              {estimatedCost != null ? `R$ ${estimatedCost.toFixed(2)}` : "—"}
            </p>
          </CardContent>
        </Card>
      </div>
      <p className="text-xs text-muted-foreground">{t("reviewWarning")}</p>
    </div>
  );
}

// --- Main wizard ---
export function CampaignNewPage() {
  const { t } = useTranslation("campaigns");
  const navigate = useNavigate();
  const { toast } = useToast();
  const createMutation = useCreateCampaign();

  const [step, setStep] = useState(0);
  const [searchParams] = useSearchParams();
  const initialFilter = useMemo<AudienceFilter>(() => {
    const raw = searchParams.get("filter");
    if (!raw) return { hasWhatsApp: true };
    try {
      const parsed = JSON.parse(decodeURIComponent(raw));
      // Normalise from explorer/inactive format into AudienceFilter
      const sf = parsed?.segment_filters ?? parsed ?? {};
      return {
        hasWhatsApp: sf.has_whatsapp ?? true,
        ownerIds: sf.owner_ids ?? undefined,
        abcCurves: sf.abc_curve ?? undefined,
        speciesIds: sf.species_ids ?? undefined,
        breedIds: sf.breed_ids ?? undefined,
        genderIds: sf.gender_ids ?? undefined,
        sizeCategoryIds: sf.size_category_ids ?? undefined,
        coatTypeIds: sf.coat_type_ids ?? undefined,
        petStatus: sf.pet_status ?? undefined,
        ageGroups: sf.age_group ? [sf.age_group] : undefined,
        detailedAgeGroups: sf.detailed_age_group ? [sf.detailed_age_group] : undefined,
        ageYearsMin: sf.age_years_min ?? undefined,
        ageYearsMax: sf.age_years_max ?? undefined,
        relationshipStatuses: sf.relationship_status ? [sf.relationship_status] : undefined,
        minVisits: sf.min_visits ?? undefined,
        minAverageTicket: sf.min_average_ticket ?? undefined,
        sterilized: sf.sterilized ?? undefined,
        weightStatus: sf.weight_status ?? undefined,
        daysSinceVisitMin: sf.inactive_days ?? undefined,
      } satisfies AudienceFilter;
    } catch {
      return { hasWhatsApp: true };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [filter, setFilter] = useState<AudienceFilter>(initialFilter);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [scheduledAt, setScheduledAt] = useState<string | null>(null);
  const [name, setName] = useState("");

  const { data: templates = [] } = useWhatsAppMessageTemplates();
  const { count: audienceCount } = useAudiencePreview(filter);

  const selectedTemplate = templates.find((t) => t.id === templateId) ?? null;

  const estimatedCost = useMemo(() => {
    if (!selectedTemplate || audienceCount == null) return null;
    return estimateCampaignCost(selectedTemplate.category as MetaTemplateCategory, audienceCount);
  }, [selectedTemplate, audienceCount]);

  async function handleFinish() {
    if (!templateId || !name.trim()) return;
    try {
      await createMutation.mutateAsync({
        name: name.trim(),
        templateId,
        audienceFilter: filter,
        estimatedRecipients: audienceCount ?? undefined,
        scheduledAt,
        estimatedCostBrl: estimatedCost ?? undefined,
      });
      toast({ title: scheduledAt ? t("successScheduled") : t("successCreated") });
      navigate("/reports/campaigns");
    } catch {
      toast({ title: t("errorCreate"), variant: "destructive" });
    }
  }

  const canAdvance = [
    true,
    !!templateId,
    true,
    !!name.trim(),
  ];

  return (
    <div className="max-w-2xl mx-auto space-y-8 py-6">
      {/* Stepper header */}
      <div className="flex items-center gap-2">
        {STEPS.map((stepKey, i) => (
          <div key={stepKey} className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => i < step && setStep(i)}
              className={cn(
                "flex items-center gap-1.5 text-sm rounded-full px-3 py-1 transition-colors",
                i === step
                  ? "bg-primary text-primary-foreground font-medium"
                  : i < step
                  ? "text-primary hover:underline"
                  : "text-muted-foreground cursor-default"
              )}
            >
              <span className="text-xs">{i + 1}.</span>
              {t(stepKey)}
            </button>
            {i < STEPS.length - 1 && <span className="text-muted-foreground text-xs">→</span>}
          </div>
        ))}
      </div>

      {/* Step content */}
      <div>
        {step === 0 && <AudienceStep filter={filter} onChange={setFilter} />}
        {step === 1 && <TemplateStep selectedId={templateId} onSelect={setTemplateId} />}
        {step === 2 && <ScheduleStep scheduledAt={scheduledAt} onChange={setScheduledAt} />}
        {step === 3 && (
          <ReviewStep
            name={name}
            onNameChange={setName}
            audienceCount={audienceCount}
            templateName={selectedTemplate?.name ?? null}
            scheduledAt={scheduledAt}
            estimatedCost={estimatedCost}
          />
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-4 border-t">
        {step > 0 ? (
          <Button variant="outline" onClick={() => setStep(step - 1)}>
            ← Voltar
          </Button>
        ) : (
          <Button variant="ghost" onClick={() => navigate("/reports/campaigns")}>
            Cancelar
          </Button>
        )}
        {step < STEPS.length - 1 ? (
          <Button disabled={!canAdvance[step]} onClick={() => setStep(step + 1)}>
            Próximo →
          </Button>
        ) : (
          <Button
            disabled={!canAdvance[step] || createMutation.isPending}
            onClick={handleFinish}
          >
            {scheduledAt ? t("confirmAndSchedule") : t("confirmAndSend")}
          </Button>
        )}
      </div>
    </div>
  );
}
